import { useState, useCallback } from 'react';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

const API = APP_CONFIG.BACKEND_URL;

export interface ConversationMeta {
  id: string;
  titulo: string;
  preview?: string;
  es_favorita: boolean;
  message_count: number;
  estado: string;
  fecha_creacion: string;
  fecha_actualizacion: string;
}

export function useConversation(userId: string) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [conversations, setConversations] = useState<ConversationMeta[]>([]);

  const createConversation = useCallback(async (): Promise<string> => {
    const res = await authFetch(`${API}/api/v1/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Error al crear conversación' }));
      throw new Error(err.detail || 'Error al crear conversación');
    }
    const data = await res.json();
    setConversationId(data.id);
    setIsFavorite(false);
    return data.id;
  }, [userId]);

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId) return conversationId;
    return createConversation();
  }, [conversationId, createConversation]);

  const saveMessage = useCallback(async (convId: string, role: string, content: string, metadata?: Record<string, unknown>): Promise<number | null> => {
    try {
      const res = await authFetch(`${API}/api/v1/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, content, metadata }),
      });
      if (res.ok) {
        const data = await res.json();
        return data.id || null;
      }
    } catch (err) {
      console.error('Error saving message:', err);
    }
    return null;
  }, []);

  const toggleFavorite = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await authFetch(`${API}/api/v1/conversations/${conversationId}/favorite`, {
        method: 'POST',
      });
      const data = await res.json();
      setIsFavorite(data.es_favorita);
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  }, [conversationId]);

  const deleteConversationById = useCallback(async (convId: string) => {
    try {
      await authFetch(`${API}/api/v1/conversations/${convId}`, { method: 'DELETE' });
      if (convId === conversationId) {
        setConversationId(null);
        setIsFavorite(false);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  }, [conversationId]);

  const renameConversation = useCallback(async (convId: string, titulo: string) => {
    try {
      await authFetch(`${API}/api/v1/conversations/${convId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titulo }),
      });
    } catch (err) {
      console.error('Error renaming conversation:', err);
    }
  }, []);

  const logActivity = useCallback(async (action: string, description: string, userName: string = '', metadata?: Record<string, unknown>, convId?: string) => {
    try {
      await authFetch(`${API}/api/v1/metrics/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, action, user_name: userName, description, metadata, conversation_id: convId }),
      });
    } catch (err) {
      console.error('Error logging activity:', err);
    }
  }, [userId]);

  const downloadConversation = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await authFetch(`${API}/api/v1/conversations/${conversationId}/download`);
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversacion_${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading conversation:', err);
    }
  }, [conversationId]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/v1/conversations?user_id=${userId}`);
      const data = await res.json();
      setConversations(data);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, [userId]);

  const loadConversation = useCallback(async (convId: string) => {
    try {
      const res = await authFetch(`${API}/api/v1/conversations/${convId}`);
      if (!res.ok) return null;
      const data = await res.json();
      setConversationId(convId);
      setIsFavorite(data.es_favorita);
      return data;
    } catch (err) {
      console.error('Error loading conversation:', err);
      return null;
    }
  }, []);

  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setIsFavorite(false);
  }, []);

  return {
    conversationId,
    isFavorite,
    conversations,
    createConversation,
    ensureConversation,
    saveMessage,
    toggleFavorite,
    deleteConversationById,
    renameConversation,
    downloadConversation,
    loadConversations,
    loadConversation,
    startNewConversation,
    logActivity,
  };
}
